<template>
  <UList>
    <UBreadcrumbs :items="breadcrumbItems" />

    <UProgressBar
      v-if="isDataReady"
      green
      is-loader
    />

    <div
      v-else
      class="w-full"
    >
      <UContent :has-title-delimiter="true">
        <template #header>
          Deposit
        </template>
        <template #content>
          <div>Choose available currency to add funds to your account.</div>
          <URow>
            <div class="left">
              <div class="one">
                <UInput
                  placeholder="Select currency"
                  type="select"
                  :options="currencyOpt"
                  @update:modelValue="(v) => (currency = v)"
                />
              </div>
              <div class="two">
                <UInput
                  v-model="amount"
                  type="number"
                  placeholder="Enter amount"
                />
              </div>
            </div>
            <UList class="right">
              <BankCard @cardNumberChange="onCardNumberChange" />
              <div v-if="!isNotRwa">
                We do not accept credit card payments.
              </div>
              <div>
                By confirming your deposit, you agree to the
                <a
                  class="link"
                  :href="`${hostLink}payment-terms`"
                  target="_blank"
                >Payment terms</a>
              </div>
              <UButton
                :disabled="!isBtnActive"
                @click="onDeposit"
              >
                Deposit
              </UButton>
            </UList>
          </URow>
        </template>
      </UContent>
    </div>
  </UList>
</template>

<script setup>
import BankCard from "@/components/buyForm/BankCard.vue";
import URow from "@/components/uikit//uLayouts/URow.vue";
import { checkAccess, scope } from "@/help/accessData";
import { computed, ref } from "vue";
import { useI18n } from "@/localization/localizator";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { useToast } from "vue-toastification";

const t = useI18n();
const store = useStore();
const toast = useToast();
const router = useRouter();
const isDataReady = ref(false);
const loading = ref(false);
const amount = ref(null);
const currency = ref(null);
const cardNumber = ref(null);

// Computeds
const breadcrumbItems = computed(() => [
  { key: 0, label: t("router.pages.dashboard"), route: "/" },
  { key: 1, label: t("router.pages.briefcase"), route: "/my-assets" },
  { key: 2, label: "Deposit", route: "/my-assets/card-buy-form" }
]);

const isNotRwa = computed(() => checkAccess(scope.notRwa));

const hostLink = computed(() => process.env.VUE_APP_HOST);
const tokens = computed(() => store.getters.tokens);
const currencyOpt = computed(() => {
  return tokens.value.map((item) => ({
    display: item.nameEng,
    value: item.mnemonicEng
  }));
});

const isBtnActive = computed(() => amount.value && currency.value);

// Methods
const onDeposit = async () => {
  loading.value = true;

  try {
    const resp = await store.dispatch("deposit/depositWithBankCard", {
      amount: String(amount.value),
      networkName: "bank_card",
      tokenSymbolToReceive: currency.value,
      tokenSymbolToSend: currency.value
    });
    if (resp.status === 267) {
      toast.error((resp?.data?.error ?? resp?.data?.message) ?? "Unknown error");
    }
    else {
      toast.success("Successful replenishment");
      setTimeout(() => {
        router.push("/transactions?type=all");
      }, 5000);
    }
  }
  catch (error) {
    toast.error("An error has occurred", error?.messages);
    console.error("Error:", error);
  }
  finally {
    loading.value = false;
  }
};

const onCardNumberChange = (value) => {
  cardNumber.value = value;
};
</script>

<style scoped lang="scss">
.left,
.right {
  flex: 1 1 0;
  box-sizing: border-box;
}

.left {
  display: flex;
  gap: 8px;

  .one {
    flex: 0 0 20%;
  }

  .two {
    flex: 1;
  }
}

.link {
  text-decoration: underline !important;
  color: var(--text-color);
}
</style>
